/**
 * Export utilities - Funzioni per esportazione dati in CSV/Excel
 */

/**
 * Converte un array di oggetti in stringa CSV
 * @param {Array} data - Array di oggetti da convertire
 * @param {Array} columns - Colonne da includere [{key, label, format?}]
 * @returns {string} - Stringa CSV
 */
export function convertToCSV(data, columns) {
  if (!data || data.length === 0) return "";

  // Header
  const headers = columns.map((col) => `"${col.label}"`).join(";");

  // Rows
  const rows = data.map((row) => {
    return columns
      .map((col) => {
        let value = row[col.key];

        // Applica formattazione se definita
        if (col.format && value !== null && value !== undefined) {
          value = col.format(value, row);
        }

        // Gestisci valori null/undefined
        if (value === null || value === undefined) {
          return '""';
        }

        // Converti oggetti
        if (typeof value === "object") {
          value = JSON.stringify(value);
        }

        // Escape caratteri speciali per CSV
        const stringValue = String(value)
          .replace(/"/g, '""') // Escape double quotes
          .replace(/\n/g, " "); // Sostituisci newlines

        return `"${stringValue}"`;
      })
      .join(";");
  });

  return [headers, ...rows].join("\n");
}

/**
 * Scarica una stringa come file
 * @param {string} content - Contenuto del file
 * @param {string} filename - Nome del file
 * @param {string} mimeType - Tipo MIME
 */
export function downloadFile(
  content,
  filename,
  mimeType = "text/csv;charset=utf-8;",
) {
  const blob = new Blob(["\ufeff" + content], { type: mimeType }); // BOM per Excel
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}

/**
 * Esporta dati in CSV
 * @param {Array} data - Dati da esportare
 * @param {Array} columns - Colonne [{key, label, format?}]
 * @param {string} filename - Nome del file (senza estensione)
 */
export function exportToCSV(data, columns, filename = "export") {
  const csv = convertToCSV(data, columns);
  const timestamp = new Date().toISOString().split("T")[0];
  downloadFile(csv, `${filename}_${timestamp}.csv`);
}

/**
 * Esporta dati in formato Excel-compatibile (XLSX via CSV con BOM)
 * @param {Array} data - Dati da esportare
 * @param {Array} columns - Colonne [{key, label, format?}]
 * @param {string} filename - Nome del file (senza estensione)
 */
export function exportToExcel(data, columns, filename = "export") {
  // Per un vero XLSX servirebbero librerie come xlsx o exceljs
  // Questa versione usa CSV con BOM che Excel apre correttamente
  const csv = convertToCSV(data, columns);
  const timestamp = new Date().toISOString().split("T")[0];
  downloadFile(
    csv,
    `${filename}_${timestamp}.xlsx`,
    "application/vnd.ms-excel;charset=utf-8;",
  );
}

/**
 * Formattatori comuni per l'export
 */
export const formatters = {
  // Formatta data
  date: (value) => {
    if (!value) return "";
    return new Date(value).toLocaleDateString("it-IT");
  },

  // Formatta data e ora
  datetime: (value) => {
    if (!value) return "";
    return new Date(value).toLocaleString("it-IT");
  },

  // Formatta valuta
  currency: (value) => {
    if (value === null || value === undefined) return "";
    return `€ ${Number(value).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  },

  // Formatta booleano
  boolean: (value) => {
    if (value === true) return "Si";
    if (value === false) return "No";
    return "";
  },

  // Formatta oggetto (es. fornitore.nome)
  nested: (key) => (value, row) => {
    const keys = key.split(".");
    let result = row;
    for (const k of keys) {
      result = result?.[k];
    }
    return result || "";
  },
};

/**
 * Configurazione colonne per export liste
 */
export const exportConfigs = {
  interventi: [
    { key: "nome", label: "Nome Intervento" },
    { key: "rsa", label: "RSA", format: formatters.nested("rsa.nome") },
    {
      key: "fornitore",
      label: "Fornitore",
      format: formatters.nested("fornitore.nome"),
    },
    { key: "tipologiaIntervento", label: "Tipologia" },
    {
      key: "dataIntervento",
      label: "Data Intervento",
      format: formatters.date,
    },
    { key: "stato", label: "Stato" },
    { key: "validato", label: "Validato", format: formatters.boolean },
    { key: "note", label: "Note" },
  ],

  contratti: [
    { key: "codice", label: "Codice" },
    { key: "nome", label: "Nome Contratto" },
    {
      key: "fornitore",
      label: "Fornitore",
      format: formatters.nested("fornitore.nome"),
    },
    { key: "tipologiaFornitura", label: "Tipologia" },
    {
      key: "dataDecorrenza",
      label: "Data Decorrenza",
      format: formatters.date,
    },
    { key: "dataFine", label: "Data Fine", format: formatters.date },
    {
      key: "importoAnnuo",
      label: "Importo Annuo",
      format: formatters.currency,
    },
    {
      key: "stato",
      label: "Stato",
      format: (v) => {
        const stati = {
          100000000: "Attivo",
          100000001: "In scadenza",
          100000002: "Scaduto",
          100000003: "Annullato",
        };
        return stati[v] || v;
      },
    },
  ],

  scadenze: [
    { key: "nome", label: "Nome Scadenza" },
    { key: "rsa", label: "RSA", format: formatters.nested("rsa.nome") },
    {
      key: "fornitore",
      label: "Fornitore",
      format: formatters.nested("fornitore.nome"),
    },
    { key: "tipologiaIntervento", label: "Tipologia" },
    { key: "dataScadenza", label: "Data Scadenza", format: formatters.date },
    {
      key: "stato",
      label: "Stato",
      format: (v) => {
        const stati = {
          100000000: "Pianificato",
          100000001: "In scadenza",
          100000002: "Scaduto",
          100000003: "Eseguito",
          100000004: "Annullato",
        };
        return stati[v] || v;
      },
    },
    {
      key: "priorita",
      label: "Priorita",
      format: (v) => {
        const prio = {
          100000000: "Bassa",
          100000001: "Media",
          100000002: "Alta",
          100000003: "Urgente",
        };
        return prio[v] || v;
      },
    },
  ],

  fornitori: [
    { key: "nome", label: "Ragione Sociale" },
    { key: "codiceInterno", label: "Codice Interno" },
    { key: "partitaIva", label: "P.IVA" },
    { key: "codiceFiscale", label: "Codice Fiscale" },
    { key: "macrocategoria", label: "Macrocategoria" },
    { key: "stato", label: "Stato" },
    { key: "emailPrincipale", label: "Email Principale" },
    { key: "emailOperativa", label: "Email Operativa" },
    { key: "pec", label: "PEC" },
    { key: "telefono", label: "Telefono" },
    { key: "indirizzoSedeLegale", label: "Indirizzo Sede Legale" },
    { key: "comuneSedeLegale", label: "Comune" },
    { key: "provinciaSedeLegale", label: "Provincia" },
    { key: "capSedeLegale", label: "CAP" },
    { key: "nazione", label: "Nazione" },
    { key: "indirizzoOperativo", label: "Indirizzo Operativo" },
    { key: "referentePrincipale", label: "Referente Principale" },
    { key: "emailReferente", label: "Email Referente" },
    { key: "telefonoReferente", label: "Tel. Referente" },
    { key: "abilitatoPortale", label: "Abilitato Portale" },
    { key: "rsaAssociate", label: "RSA Associate" },
    { key: "tipologieIntervento", label: "Tipologie Intervento" },
    { key: "referenti", label: "Referenti" },
    { key: "note", label: "Note" },
  ],

  rsa: [
    { key: "nome", label: "Nome RSA" },
    { key: "codice", label: "Codice" },
    { key: "indirizzo", label: "Indirizzo" },
    { key: "citta", label: "Citta" },
    { key: "provincia", label: "Provincia" },
    { key: "cap", label: "CAP" },
    { key: "telefono", label: "Telefono" },
    { key: "email", label: "Email" },
    { key: "direttore", label: "Direttore" },
  ],
};

export default {
  convertToCSV,
  downloadFile,
  exportToCSV,
  exportToExcel,
  formatters,
  exportConfigs,
};
